import type { ElementType, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import {
  MetricSkeleton,
  TextSkeleton,
} from "@/components/shared/loading-skeleton";
import { AnimatedNumber } from "@/components/shared/AnimatedNumber";
import { cn } from "@/lib/utils";

export type StatCardAccent = "primary" | "secondary" | "accent" | "muted";

const accentClasses: Record<StatCardAccent, string> = {
  primary: "bg-primary/10 text-primary",
  secondary: "bg-secondary/30 text-secondary-foreground",
  accent: "bg-accent/10 text-accent",
  muted: "bg-muted text-muted-foreground",
};

interface StatCardProps {
  label: string;
  value: number | string | undefined;
  sub?: string;
  icon?: ElementType;
  accent?: StatCardAccent;
  isLoading?: boolean;
  to?: string;
  className?: string;
  footer?: ReactNode;
}

export function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  accent = "primary",
  isLoading,
  to,
  className,
  footer,
}: StatCardProps) {
  const content = (
    <Card
      className={cn(
        "stat-card",
        to && "cursor-pointer hover:shadow-md",
        className,
      )}
    >
      <CardContent className="p-5 text-right" dir="rtl">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-medium text-muted-foreground">
            {label}
          </p>
          {Icon && (
            <div
              className={cn(
                "rounded-md p-1.5 shrink-0",
                accentClasses[accent],
              )}
            >
              <Icon className="size-3.5" />
            </div>
          )}
        </div>
        {isLoading ? (
          <div className="mt-2 space-y-2">
            <MetricSkeleton />
            {sub && <TextSkeleton className="h-3 w-32" />}
          </div>
        ) : (
          <>
            <p className="mt-2 text-3xl font-bold text-foreground">
              {typeof value === "number" ? (
                <AnimatedNumber value={value} isLoading={isLoading} />
              ) : (
                (value ?? "—")
              )}
            </p>
            {sub && (
              <p className="mt-1 text-xs text-muted-foreground">{sub}</p>
            )}
          </>
        )}
        {footer}
      </CardContent>
    </Card>
  );

  if (to) {
    return <Link to={to}>{content}</Link>;
  }
  return content;
}
